import { createFileRoute, useParams } from '@tanstack/react-router'
import { Eye } from 'lucide-react'
import { usePublishViewModeBanner } from '@/components/ViewModeBanner'
import {
  AdminAuditorViewExitLink,
} from '@/components/auditor-center/AdminAuditorViewBanner'
import { AuditorCycleBreadcrumb } from '@/components/auditor-center/AuditorCycleBreadcrumb'
import { AuditorCycleTopBar } from '@/components/auditor-center/AuditorCycleTopBar'
import { ScreenataPageLayout } from '@/components/compliance/ScreenataPageLayout'
import { ScreenataPoliciesContent } from '@/components/screenata/ScreenataPoliciesContent'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/auditor-center/$auditCycleId_/policies/'
)({
  component: AuditorPoliciesRoutePage,
})

function AuditorPoliciesRoutePage() {
  const { workspaceId, auditCycleId } = useParams({
    from: '/(protected)/workspaces/$workspaceId/auditor-center/$auditCycleId_/policies/',
  })
  const { isAdmin, isOwner, isAuditor } = useWorkspacePermissions(workspaceId)
  const showAdminAuditorViewHeader = (isAdmin || isOwner) && !isAuditor

  // This route sits under `$auditCycleId_` — the trailing underscore escapes the
  // cycle layout — so it is NOT inside `AuditorCenterCycleShell` and has to
  // publish the banner itself. That escape hatch is why the old header-tint
  // approach fragmented across call sites in the first place.
  usePublishViewModeBanner(
    'preview',
    showAdminAuditorViewHeader
      ? {
          icon: Eye,
          label: 'Auditor preview',
          description: 'This is what your auditor sees on your submission',
          action: (
            <AdminAuditorViewExitLink
              workspaceId={workspaceId}
              auditCycleId={auditCycleId}
            />
          ),
        }
      : null,
    [showAdminAuditorViewHeader, workspaceId, auditCycleId],
  )
  const auditsHomeRoute =
    isAdmin || isOwner
      ? '/workspaces/$workspaceId/audits'
      : '/workspaces/$workspaceId/auditor-center'

  return (
    <ScreenataPageLayout
      title='Audits'
      customHeader={
        <AuditorCycleTopBar
          breadcrumb={
            <AuditorCycleBreadcrumb
              workspaceId={workspaceId}
              auditsHomeRoute={auditsHomeRoute}
              items={[{ label: 'Policies', current: true }]}
            />
          }
        />
      }
      raw
      scroll
    >
      <ScreenataPoliciesContent
        workspaceId={workspaceId}
        mode='readonly'
        detailRoute='auditor'
        auditCycleId={auditCycleId}
      />
    </ScreenataPageLayout>
  )
}
